import { sync_config_route, VITE_API_URL } from "@/constants";
import type { AuthFetchParams } from "./global";
import { isTokenInvalid } from "@/lib/api-client";
import type { SyncConfig } from "@/lib/types";
import { queryClient } from "@/main";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import i18n from "@/i18n";

async function getSyncConfig(pcId: string, params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${sync_config_route}/${pcId}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  if (!res.ok) {
    throw new Error("Failed to load sync settings");
  }

  return (await res.json()) as SyncConfig;
}

export const syncConfigQueryOpts = (pcId: string, params: AuthFetchParams) =>
  queryOptions({
    queryKey: [params.orgId, "syncConfig", pcId],
    queryFn: async () => await getSyncConfig(pcId, params),
  });

export const useSyncConfigUpdateMutation = () => {
  const { getToken, setToken, handleTokenInvalid } = useAuth();
  const { orgId } = useOrg();

  return useMutation({
    mutationFn: async (variables: {
      pc_id: string;
      title: boolean;
      description: boolean;
      price: boolean;
      quantity: boolean;
      end_listing_when_quantity_zero: boolean;
      auto_export_enabled?: boolean;
      export_targets?: string[];
      external_order_import: boolean;
    }) => {
      const res = await fetch(`${VITE_API_URL}${sync_config_route}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          "X-Inventoree-Organization": orgId,
        },
        credentials: "include",
        body: JSON.stringify(variables),
      });

      if (await isTokenInvalid(res)) {
        await handleTokenInvalid();
        return;
      }

      const access = res.headers.get("access-token");
      if (access) setToken(access);

      if (!res.ok) {
        throw new Error("Failed to update sync settings");
      }

      return (await res.json()) as SyncConfig;
    },
    onSuccess: (data, variables) => {
      if (!data) {
        return;
      }

      toast.success(i18n.t("toast.syncSettingsUpdated"));
      queryClient.setQueryData([orgId, "syncConfig", variables.pc_id], data);
      queryClient.invalidateQueries({ queryKey: [orgId, "syncConfig"] });
    },
    onError: () => {
      toast.error(i18n.t("toast.syncSettingsFailed"));
    },
  });
};
